/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2023. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApiService } from 'app/services/api.service';
import { Invoice, InvoiceItemCustomization, InvoiceItemCustomizationMultiAction } from '@core/models';
import { IApiResponse } from '@truckstop-tms/zuum-lib';

@Injectable({
  providedIn: 'root',
})
export class InvoiceItemCustomizationService {
  constructor(private http: HttpClient, private apiService: ApiService) {}

  getInvoiceItemCustomizations(clientId: number): Observable<IApiResponse<InvoiceItemCustomization[]>> {
    return this.http
      .get<IApiResponse<InvoiceItemCustomization[]>>(
        `${this.apiService.superAdminApi}/invoice-item-customization/${clientId}`
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  createItem(payload: Partial<InvoiceItemCustomization>): Observable<IApiResponse<InvoiceItemCustomization>> {
    return this.http
      .post<IApiResponse<InvoiceItemCustomization>>(
        `${this.apiService.superAdminApi}/invoice-item-customization`,
        payload
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updateItem(payload: Partial<InvoiceItemCustomization>): Observable<IApiResponse<InvoiceItemCustomization>> {
    return this.http
      .put<IApiResponse<InvoiceItemCustomization>>(
        `${this.apiService.superAdminApi}/invoice-item-customization`,
        payload
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  duplicateItem(payload: Partial<InvoiceItemCustomization>): Observable<IApiResponse<InvoiceItemCustomization>> {
    return this.http
      .post<IApiResponse<InvoiceItemCustomization>>(
        `${this.apiService.superAdminApi}/invoice-item-customization/duplicate`,
        payload
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  deleteItem(id: number): Observable<IApiResponse<{ id: number; deletedInvoiceId: number }>> {
    return this.http
      .delete<IApiResponse<{ id: number; deletedInvoiceId: number }>>(
        `${this.apiService.superAdminApi}/invoice-item-customization/${id}`
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  removeItemFromInvoice(
    id: number
  ): Observable<IApiResponse<{ item: InvoiceItemCustomization; deletedInvoiceId: number | null }>> {
    return this.http
      .post<IApiResponse<{ item: InvoiceItemCustomization; deletedInvoiceId: number | null }>>(
        `${this.apiService.superAdminApi}/invoice-item-customization/invoice/remove`,
        { id }
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  moveItemToInvoice(
    invoice: Invoice,
    item: InvoiceItemCustomization
  ): Observable<IApiResponse<{ item: InvoiceItemCustomization; deletedInvoiceId: number | null }>> {
    return this.http
      .post<IApiResponse<{ item: InvoiceItemCustomization; deletedInvoiceId: number | null }>>(
        `${this.apiService.superAdminApi}/invoice-item-customization/invoice/move`,
        { invoice, item }
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  addItemToInvoice(
    invoice: Invoice,
    item: InvoiceItemCustomization
  ): Observable<IApiResponse<InvoiceItemCustomization>> {
    return this.http
      .post<IApiResponse<InvoiceItemCustomization>>(
        `${this.apiService.superAdminApi}/invoice-item-customization/invoice/add`,
        { invoice, item }
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  multiAction(
    clientId: number,
    payload: InvoiceItemCustomizationMultiAction
  ): Observable<
    IApiResponse<{ items: InvoiceItemCustomization[]; deletedInvoices: number[]; createdInvoice: Invoice }>
  > {
    return this.http
      .post<IApiResponse<{ items: InvoiceItemCustomization[]; deletedInvoices: number[]; createdInvoice: Invoice }>>(
        `${this.apiService.superAdminApi}/invoice-item-customization/multi-action/${clientId}`,
        payload
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }
}
